import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

// "2026-09-20" → stored at noon so it can never slip to a neighboring day
function dateToTakenAt(dateStr) {
  return dateStr ? `${dateStr}T12:00:00.000Z` : null;
}

export default function Upload() {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState("");
  const [newAlbumName, setNewAlbumName] = useState("");
  const [newAlbumDate, setNewAlbumDate] = useState("");
  const [newAlbumDesc, setNewAlbumDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState("");
  const [fallbackDate, setFallbackDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => { fetchAlbums(); }, []);

  async function fetchAlbums() {
    const { data } = await supabase.from("albums").select("*").order("date", { ascending: false });
    setAlbums(data || []);
  }

  const albumForUpload = albums.find((a) => String(a.id) === String(selectedAlbum));
  const albumDate = albumForUpload?.date ? String(albumForUpload.date).slice(0, 10) : null;

  async function createAlbum(e) {
    e.preventDefault();
    setCreatingAlbum(true);
    const { data, error } = await supabase
      .from("albums")
      .insert({ name: newAlbumName, date: newAlbumDate, description: newAlbumDesc })
      .select()
      .single();

    if (!error) {
      setAlbums((prev) => [data, ...prev]);
      setSelectedAlbum(data.id);
      setNewAlbumName("");
      setNewAlbumDate("");
      setNewAlbumDesc("");
      setMessage({ type: "success", text: `Album "${data.name}" created!` });
    } else {
      setMessage({ type: "error", text: error.message });
    }
    setCreatingAlbum(false);
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!selectedAlbum) { setMessage({ type: "error", text: "Please select or create an album first." }); return; }
    if (files.length === 0) { setMessage({ type: "error", text: "Please select at least one photo." }); return; }

    setUploading(true);
    setMessage(null);
    setUploadProgress(0);
    let successCount = 0;
    let usedFallback = 0;
    let noDate = 0;
    const failures = [];

    const headers = await getAuthHeaders();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("photo", file);

      try {
        const res = await fetch("/api/upload", {
          method: "POST",
          headers,
          body: formData,
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          failures.push(`${file.name}: ${data.error || `server returned ${res.status}`}`);
          continue;
        }

        // Real EXIF date wins; the form's date only fills in when there isn't one
        const exifDate = data.takenDates?.[0] ?? null;
        const takenAt = exifDate || dateToTakenAt(fallbackDate);

        const row = {
          album_id: selectedAlbum,
          filename: file.name,
          file_path: data.filenames[0],
          caption: caption || null,
          taken_at: takenAt,
        };
        console.log("Saving photo:", row, exifDate ? "(from photo)" : takenAt ? "(from form)" : "(no date)");

        const { error } = await supabase.from("photos").insert(row);
        if (error) {
          console.error("Database error:", error);
          failures.push(`${file.name}: ${error.message}`);
          continue;
        }
        successCount++;
        if (!exifDate && takenAt) usedFallback++;
        if (!takenAt) noDate++;
      } catch (err) {
        console.error("Upload error:", err);
        failures.push(`${file.name}: ${err.message}`);
      }

      setUploadProgress(Math.round(((i + 1) / files.length) * 100));
    }

    let summary = `${successCount} of ${files.length} photo(s) uploaded.`;
    if (usedFallback > 0) summary += ` ${usedFallback} had no date of their own and were set to ${fallbackDate}.`;
    if (noDate > 0) summary += ` ${noDate} had no date and will show on today's date in the calendar.`;

    if (failures.length === 0) {
      setMessage({ type: "success", text: summary });
    } else {
      setMessage({ type: "error", text: `${summary} Failed: ${failures.join("; ")}` });
    }
    setFiles([]);
    setCaption("");
    setFallbackDate("");
    setUploadProgress(0);
    setUploading(false);
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Upload Photos</h1>
        <p className="page-subtitle">Admin only</p>
      </div>

      {message && (
        <div className={`message-banner ${message.type === "success" ? "message-success" : "error-banner"}`}>
          {message.type === "success" ? "✅" : "⚠️"} {message.text}
        </div>
      )}

      {/* Create Album */}
      <div className="upload-section">
        <h2 className="section-label">Create New Album</h2>
        <form className="album-form" onSubmit={createAlbum}>
          <input
            className="form-input"
            type="text"
            placeholder="Album name (e.g. First Week Home)"
            value={newAlbumName}
            onChange={(e) => setNewAlbumName(e.target.value)}
            required
          />
          <input
            className="form-input"
            type="date"
            value={newAlbumDate}
            onChange={(e) => setNewAlbumDate(e.target.value)}
            required
          />
          <input
            className="form-input"
            type="text"
            placeholder="Description (optional)"
            value={newAlbumDesc}
            onChange={(e) => setNewAlbumDesc(e.target.value)}
          />
          <button className="create-album-btn" type="submit" disabled={creatingAlbum}>
            {creatingAlbum ? "Creating..." : "Create Album"}
          </button>
        </form>
      </div>

      {/* Upload Photos */}
      <div className="upload-section">
        <h2 className="section-label">Upload Photos</h2>
        <form className="upload-form" onSubmit={handleUpload}>
          <div className="form-group">
            <label className="form-label">Select Album</label>
            <select
              className="form-input"
              value={selectedAlbum}
              onChange={(e) => setSelectedAlbum(e.target.value)}
              required
            >
              <option value="">Choose an album...</option>
              {albums.map((a) => (
                <option key={a.id} value={a.id}>{a.name} — {new Date(a.date).toLocaleDateString()}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Photos</label>
            <div
              className="drop-zone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files)); }}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                className="file-input"
                id="file-input"
                onChange={(e) => setFiles(Array.from(e.target.files))}
              />
              <label htmlFor="file-input" className="drop-label">
                {files.length > 0
                  ? `${files.length} photo(s) selected`
                  : "📸 Drag & drop or click to select photos"}
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="fallback-date">Date taken (optional)</label>
            <div className="date-taken-row">
              <input
                id="fallback-date"
                className="form-input"
                type="date"
                value={fallbackDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setFallbackDate(e.target.value)}
              />
              {albumDate && fallbackDate !== albumDate && (
                <button type="button" className="date-taken-btn" onClick={() => setFallbackDate(albumDate)}>
                  Use album date
                </button>
              )}
              {fallbackDate && (
                <button type="button" className="date-taken-btn" onClick={() => setFallbackDate("")}>
                  Clear
                </button>
              )}
            </div>
            <p className="form-hint">
              Only used for photos that don't have their own date, like ones saved from texts or social media.
              Photos with a date keep it.
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Caption (optional)</label>
            <input
              className="form-input"
              type="text"
              placeholder="e.g. First day home!"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
          </div>

          {/* Preview */}
          {files.length > 0 && (
            <div className="preview-grid">
              {files.map((file, i) => (
                <img
                  key={i}
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="preview-img"
                />
              ))}
            </div>
          )}

          {/* Progress bar */}
          {uploading && uploadProgress > 0 && (
            <div className="upload-progress">
              <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
              <span className="upload-progress-text">{uploadProgress}%</span>
            </div>
          )}

          <button className="upload-btn" type="submit" disabled={uploading}>
            {uploading ? `Uploading... ${uploadProgress}%` : `Upload ${files.length > 0 ? files.length + " Photo(s)" : "Photos"}`}
          </button>
        </form>
      </div>
    </div>
  );
}